// Shared helpers for Stripe Payment Request API (Apple Pay / Google Pay).
// Used by StripeCheckout and ProductApplePay — keep logic in one place.

import type {
  PaymentRequestItem,
  PaymentRequestShippingOption,
} from "@stripe/stripe-js";
import { ShippingRate } from "../api-client/models";

export const toMinorUnits = (amount: number): number =>
  Math.max(Math.round(amount * 100), 0);

// Illinois is the only state that charges tax.
// Apple Pay may return "IL" or "ILLINOIS" depending on device locale.
export const isIllinois = (region: string): boolean => {
  const r = region.trim().toUpperCase();
  return r === "IL" || r === "ILLINOIS";
};

export const parseShippingAmount = (amount?: string | number): number => {
  if (typeof amount === "number") return amount;
  return parseFloat(amount || "0") || 0;
};

export const buildPaymentItems = (
  subtotal: number,
  tax: number,
  shipping: number,
  discount: number,
): PaymentRequestItem[] => {
  const items: PaymentRequestItem[] = [
    { label: "Subtotal", amount: toMinorUnits(subtotal) },
    // Tax always included — shows $0.00 for non-IL addresses.
    { label: "Tax", amount: toMinorUnits(tax) },
  ];
  if (shipping > 0)
    items.push({ label: "Shipping", amount: toMinorUnits(shipping) });
  if (discount > 0)
    items.push({ label: "Discount", amount: -toMinorUnits(discount) });
  return items;
};

export const buildPaymentTotal = (
  subtotal: number,
  tax: number,
  shipping: number,
  discount: number,
): PaymentRequestItem => ({
  label: "Goal Wear",
  amount: toMinorUnits(subtotal + tax + shipping - discount),
});

export const mapRateToShippingOption = (
  rate: ShippingRate,
): PaymentRequestShippingOption => ({
  id: rate.object_id,
  label: `${rate.provider} — ${rate.service_level}`,
  detail: rate.duration_terms || `${rate.estimated_days ?? "?"} day delivery`,
  amount: toMinorUnits(parseShippingAmount(rate.amount)),
});
